import { cva, type VariantProps } from "class-variance-authority";
import { Fragment, forwardRef, type ReactNode } from "react";
import { cn } from "../../lib/utils";

const claimWrapperVariants = cva("", {
  variants: {
    color: {
      white: "text-white",
      black: "text-black",
    },
    size: {
      default:
        "font-bold text-[120px] leading-[120px] tracking-[1%] md:text-[280px] md:leading-[280px]",
    },
  },
  defaultVariants: {
    color: "black",
    size: "default",
  },
});

export interface ClaimWrapperProps
  extends VariantProps<typeof claimWrapperVariants> {
  color?: "black" | "white";
  text?: string;
  className?: string;
}

const claimHighlightPattern = /==([^=]+)==/g;

function renderClaimText(text?: string) {
  if (!text) {
    return text;
  }

  const parts: Array<{ pos: number; node: ReactNode }> = [];
  let lastIndex = 0;

  for (const match of text.matchAll(claimHighlightPattern)) {
    const [marker, highlightedText] = match;
    const matchIndex = match.index ?? 0;

    if (matchIndex > lastIndex) {
      parts.push({ pos: lastIndex, node: text.slice(lastIndex, matchIndex) });
    }

    parts.push({ pos: matchIndex, node: <mark>{highlightedText}</mark> });
    lastIndex = matchIndex + marker.length;
  }

  if (lastIndex === 0) {
    return text;
  }

  if (lastIndex < text.length) {
    parts.push({ pos: lastIndex, node: text.slice(lastIndex) });
  }

  return parts.map(({ pos, node }) => <Fragment key={pos}>{node}</Fragment>);
}

export const ClaimWrapper = forwardRef<HTMLQuoteElement, ClaimWrapperProps>(
  (props, ref) => {
    const variantClasses = claimWrapperVariants({
      color: props.color,
      size: props.size,
    });

    return (
      <blockquote
        className={cn(variantClasses, props.className, "claim font-brand")}
        ref={ref}
      >
        {renderClaimText(props.text)}
      </blockquote>
    );
  }
);

ClaimWrapper.displayName = "ClaimWrapper";
